// src/frontend/src/components/Landing/StarterKits.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { starterKits } from '../../utils/starterKits';

const StarterKits: React.FC = () => {
  const [activeKit, setActiveKit] = useState(0);

  const getColorClasses = (color: string) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-600',
        hover: 'hover:bg-blue-100'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-600',
        hover: 'hover:bg-green-100'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-600',
        hover: 'hover:bg-purple-100'
      }
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Three Polished Starter Kits
          </h2>
          <p className="mt-4 text-xl text-gray-600">
            Industry-specific scenarios designed by experienced PMs
          </p>
        </div>

        {/* Kit selector */}
        <div className="mt-12 flex flex-wrap justify-center gap-4">
          {starterKits.map((kit, index) => (
            <button
              key={kit.id}
              onClick={() => setActiveKit(index)}
              className={`px-6 py-3 rounded-lg font-medium transition duration-150 ${
                activeKit === index
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="mr-2">{kit.icon}</span>
              {kit.name}
            </button>
          ))}
        </div>

        {/* Active kit details */}
        <div className="mt-12">
          <div className="bg-gray-50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {starterKits[activeKit].name}
            </h3>
            <p className="text-lg text-gray-600 mb-8">
              {starterKits[activeKit].description}
            </p>

            {/* Scenarios grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {starterKits[activeKit].scenarios.map((scenario, index) => {
                const colors = getColorClasses(starterKits[activeKit].color);
                return (
                  <div
                    key={index}
                    className={`${colors.bg} ${colors.border} border rounded-lg p-6 ${colors.hover} transition duration-150`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-medium text-gray-900">{scenario.title}</h4>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                        scenario.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                        scenario.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {scenario.difficulty.charAt(0).toUpperCase() + scenario.difficulty.slice(1)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Stakeholder: {scenario.stakeholderType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    <p className="text-sm text-gray-700 italic">
                      "{scenario.preview}"
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Benefits */}
            <div className="border-t pt-6">
              <h4 className="font-medium text-gray-900 mb-3">What You'll Learn:</h4>
              <ul className="space-y-2">
                {starterKits[activeKit].benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Link
            to="/register"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition duration-150"
          >
            Access All Starter Kits with Free Trial
            <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StarterKits;